import { Helmet } from 'react-helmet-async';

// Use public assets for favicons
const favicon16 = '/favicon-16x16.png';
const favicon32 = '/favicon-32x32.png';
const favicon192 = '/android-chrome-192x192.png';
const favicon512 = '/android-chrome-512x512.png';
const appleTouchIcon = '/apple-touch-icon.png';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  noindex?: boolean;
}

const DEFAULT_TITLE = 'HelioROI - Global Solar Investment Analysis Platform';
const DEFAULT_DESCRIPTION = 'Professional solar energy investment analysis platform for GES investors. Real NASA POWER & Open-Meteo data, comprehensive financial modeling, production forecasting, and ROI analysis.';
const DEFAULT_KEYWORDS = 'solar investment analysis, solar energy ROI calculator, GES investment platform, photovoltaic investment analysis, solar farm financial modeling, PV system design tool, solar production forecast, renewable energy investment, solar power plant analysis, solar investment calculator, solar energy feasibility study, solar project finance, solar ROI analysis, solar energy investment software, solar PV calculator, solar farm investment, solar investment platform, solar power investment analysis, solar energy financial modeling, photovoltaic ROI calculator';
const SITE_URL = 'https://helioroi.com';
const COMPANY_NAME = 'CRRSoft, Inc.';

export function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords = DEFAULT_KEYWORDS,
  canonical,
  ogType = 'website',
  ogImage = `${SITE_URL}/og-image.png`,
  noindex = false,
}: SEOProps) {
  const fullTitle = title ? `${title} | ${DEFAULT_TITLE}` : DEFAULT_TITLE;
  const canonicalUrl = canonical ? `${SITE_URL}${canonical}` : SITE_URL;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={COMPANY_NAME} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={DEFAULT_TITLE} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:creator" content="@helioroi" />

      {/* Additional SEO Tags */}
      <meta name="application-name" content="HelioROI" />
      <meta name="apple-mobile-web-app-title" content="HelioROI" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />

      {/* Geo Tags */}
      <meta name="geo.region" content="US" />
      <meta name="geo.placename" content="United States" />
      
      {/* Language */}
      <meta httpEquiv="content-language" content="en" />
      <link rel="alternate" hrefLang="en" href={canonicalUrl} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

      {/* Favicon */}
      <link rel="icon" type="image/png" sizes="16x16" href={favicon16} />
      <link rel="icon" type="image/png" sizes="32x32" href={favicon32} />
      <link rel="icon" type="image/png" sizes="192x192" href={favicon192} />
      <link rel="icon" type="image/png" sizes="512x512" href={favicon512} />
      
      {/* Apple Touch Icon */}
      <link rel="apple-touch-icon" sizes="180x180" href={appleTouchIcon} />
      
      {/* Shortcut icon (fallback) */}
      <link rel="shortcut icon" href={favicon32} />
    </Helmet>
  );
}

// Predefined SEO configurations for common pages
export const SEO_CONFIGS = {
  home: {
    title: '',
    description: 'Professional solar power plant investment analysis platform. Calculate ROI, analyze locations, design systems & forecast production with real NASA & weather data. Trusted by GES investors worldwide.',
    keywords: 'solar investment platform, solar ROI calculator, solar energy analysis, GES investment, solar farm calculator, photovoltaic investment, solar power plant ROI',
    canonical: '/',
  },
  
  dashboard: {
    title: 'Dashboard',
    description: 'Manage your solar investment projects. View analyses, track credits, and access detailed solar energy investment reports.',
    canonical: '/dashboard',
    noindex: true,
  },

  newAnalysis: {
    title: 'New Solar Investment Analysis',
    description: 'Create comprehensive solar power plant investment analysis. Location-based solar irradiance data, system design, financial modeling & production forecasting.',
    keywords: 'solar investment calculator, solar analysis tool, solar feasibility study, solar ROI analysis, PV system calculator',
    canonical: '/new-analysis',
  },

  pricing: {
    title: 'Pricing & Credits',
    description: 'Affordable credit packages for solar investment analysis. Starter ($15), Pro ($50), Business ($100). Pay-per-use pricing model.',
    keywords: 'solar analysis pricing, solar calculator cost, solar investment tool pricing',
    canonical: '/pricing',
  },

  login: {
    title: 'Login',
    description: 'Sign in to your HelioROI account. Access your solar investment analyses and project data.',
    canonical: '/login',
    noindex: true,
  },

  signup: {
    title: 'Sign Up',
    description: 'Create your HelioROI account. Start analyzing solar power plant investments with real NASA data.',
    canonical: '/signup',
    noindex: true,
  },

  terms: {
    title: 'Terms of Service',
    description: 'Terms of Service for HelioROI Investment Analysis Platform by CRRSoft, Inc.',
    canonical: '/terms',
  },

  privacy: {
    title: 'Privacy Policy',
    description: 'Privacy Policy for HelioROI Investment Analysis Platform. Learn how we protect your data.',
    canonical: '/privacy',
  },
};
